import React from 'react';
import Header from '@/components/common/Header';
import SEOHead from '@/components/common/SEOHead';
import FooterSection from '@/components/homepage/FooterSection';
import { useAnalytics } from '@/hooks/useAnalytics';

const TermsOfService = () => {
  const { trackNavigation } = useAnalytics(); // This also handles page view tracking
  
  return (
    <div className="min-h-screen bg-[#F7F5F2] font-nunito">
      <SEOHead pageKey="terms" />
      <Header showActions={true} />
      
      <main className="pt-[80px]">
        {/* Header */}
        <div className="section-container py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-[32px] md:text-[72px] font-bold text-[#6C4F77] mb-4 leading-tight">Terms of Service</h1>
            <p className="text-[#2C2C2C] text-lg">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white py-12 mx-[20px] md:mx-0 rounded-[8px] md:rounded-none">
          <div className="section-container">
            <div className="max-w-4xl mx-auto space-y-8">
            <section>
              <p className="text-[#2C2C2C] leading-relaxed">
                Welcome to Wabisabi ("we," "our," or "us"). These Terms of Service ("Terms") govern your use of our website and application (collectively, the "Service"). By accessing or using Wabisabi, you agree to be bound by these Terms. If you don't agree to these Terms, please don't use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">1. Acceptance of Terms</h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                By creating an account or using Wabisabi, you confirm that you are at least 13 years old and have the legal capacity to enter into these Terms. If you're using Wabisabi on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">2. Description of Service</h2>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">
                Wabisabi is a relationship management platform that helps you remember what matters about the people in your life. Our Service includes:
              </p>
              <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                <li>Contact management and organization</li>
                <li>Interaction tracking and memory keeping</li>
                <li>Intelligent reminders and suggestions</li>
                <li>Personal relationship insights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">3. User Accounts</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-[#2C2C2C] mb-2">Account Creation</h3>
                  <p className="text-[#2C2C2C] leading-relaxed">
                    You're responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#2C2C2C] mb-2">Account Information</h3>
                  <p className="text-[#2C2C2C] leading-relaxed">
                    You agree to provide accurate, current, and complete information during registration and to update such information to maintain its accuracy.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">4. Acceptable Use</h2>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">
                You agree to use Wabisabi only for lawful purposes and in accordance with these Terms. You agree NOT to:
              </p>
              <ul className="list-disc list-inside text-[#2C2C2C] space-y-2 ml-4">
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Upload, post, or transmit any content that is harmful, offensive, or violates others' rights</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Share personal information about others without their consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">5. User Content</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-[#2C2C2C] mb-2">Your Content</h3>
                  <p className="text-[#2C2C2C] leading-relaxed">
                    You retain ownership of all content you submit to Wabisabi, including contacts, notes, photos, and interactions. By using our Service, you grant us a limited license to store, process, and display your content solely to provide the Service to you.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#2C2C2C] mb-2">Content Responsibility</h3>
                  <p className="text-[#2C2C2C] leading-relaxed">
                    You're responsible for all content you submit and must ensure you have the right to share it. We reserve the right to remove content that violates these Terms or applicable laws.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">6. Privacy and Data</h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                Your privacy is important to us. Our collection and use of your personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using Wabisabi, you consent to the collection and use of your information as described in our Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">7. Intellectual Property</h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                Wabisabi and all related content, features, and functionality are owned by us and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works based on our Service without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">8. Service Availability</h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                We strive to provide reliable access to Wabisabi, but we don't guarantee that the Service will be available at all times. We may modify, suspend, or discontinue the Service at any time, with or without notice, for maintenance, updates, or other reasons.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">9. Termination</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-[#2C2C2C] mb-2">By You</h3>
                  <p className="text-[#2C2C2C] leading-relaxed">
                    You may terminate your account at any time by contacting us or using the account deletion feature in the app.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#2C2C2C] mb-2">By Us</h3>
                  <p className="text-[#2C2C2C] leading-relaxed">
                    We may terminate or suspend your account if you violate these Terms or for any other reason at our discretion, with or without notice.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">10. Disclaimers and Limitation of Liability</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-[#2C2C2C] mb-2">Service "As Is"</h3>
                  <p className="text-[#2C2C2C] leading-relaxed">
                    Wabisabi is provided "as is" without warranties of any kind. We don't guarantee that the Service will meet your requirements or be error-free.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#2C2C2C] mb-2">Limitation of Liability</h3>
                  <p className="text-[#2C2C2C] leading-relaxed">
                    To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of Wabisabi.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">11. Indemnification</h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of the Service, your violation of these Terms, or your violation of any rights of another party.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">12. Governing Law</h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                These Terms are governed by the laws of Germany. Any disputes arising from these Terms or your use of Wabisabi will be resolved in the courts of Berlin, Germany.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">13. Changes to Terms</h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                We may update these Terms from time to time. If we make material changes, we'll notify you via email or through the Service. Your continued use of Wabisabi after such changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2C2C2C] mb-4">14. Contact Us</h2>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-4 p-4 bg-[#F7F5F2] rounded-lg">
                <p className="text-[#2C2C2C]">
                  <strong>Email:</strong> <a href="mailto:hello@appwabisabi.com" className="text-[#2C2C2C] hover:underline">hello@appwabisabi.com</a><br />
                  <strong>Address:</strong> Bismarck Strasse 87, 10627, Berlin, Germany
                </p>
              </div>
            </section>

            <section className="border-t pt-6">
              <p className="text-sm text-[#2C2C2C]/60 text-center">
                By using Wabisabi, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </section>
            </div>
          </div>
        </div>
      </main>
      <FooterSection />
    </div>
  );
};

export default TermsOfService;